import { type ReactNode, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/shared/utils/logger';
import {
  newVideoSubmitIdempotencyKey,
  submitToValoiEngine,
} from '@/features/generation/services/submissionService';
import { useNotifications } from '@/app/providers/notifications';
import { ProcessingContext, type ProcessingContextValue } from '@/app/providers/processing';
import { useCurrentUser } from '@/app/providers/user';
import { useWallet } from '@/app/providers/wallet';
import type { LibraryItem, ProcessingJob, ProcessingStatus, SubmissionPayload } from '@/shared/types';
import { fetchVideoStatus, resumeVideo, confirmVideo, cancelVideo, deleteVideo } from '@/features/content/services/videoApi';
import type { JobStatusResult } from '@/features/content/services/videoApi';
import { ApiError } from '@/shared/api/apiClient';
import { describeFromText, mergeMetadata, titleFromText } from '@/features/library/services/libraryApi';
import { isTerminalStatus } from '@/shared/api/backendContract';
import { openVideoSocket } from '@/features/content/services/videoSocket';
import { fetchLibraryItems, fetchLibraryItem, upsertLibraryItem, updateLibraryItemStatus, removeLibraryItem } from '@/features/library/services/libraryService';
import { UserFacingError } from '@/shared/utils/errors';

const HYDRATED_ETA_SECONDS = 120;

// Maps a cached/library item into the in-session job shape the poller tracks.
// Shared by the mount hydration and the resume path so they can't diverge.
function libraryItemToJob(item: LibraryItem): ProcessingJob {
  return {
    id: item.id,
    title: item.title,
    status: item.status ?? 'queued',
    etaSeconds: HYDRATED_ETA_SECONDS,
    submittedAt: new Date(item.createdAt).getTime(),
    videoId: item.id,
    aspectRatio: item.aspectRatio ?? '16:9',
    /* Same name, two meanings: on a ProcessingJob `durationSeconds` is the
       requested seconds *per scene* that drives the ETA and the price, while on a
       LibraryItem it is the finished video's runtime. Read it from the generation
       metadata, which is where the setting actually lives. */
    durationSeconds: item.metadata?.duration ?? 10,
    text: item.sourceText ?? '',
    metadata: item.metadata,
    videoUrl: item.videoUrl,
    thumbnailUrl: item.thumbnailUrl,
  };
}

export function ProcessingProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<ProcessingJob[]>([]);
  const jobsRef = useRef<ProcessingJob[]>(jobs);
  const { pushNotification } = useNotifications();
  const { user } = useCurrentUser();
  const { refreshBalance } = useWallet();
  // Keep the current user_id available to the polling closures without making
  // them stale; library writes are namespaced per user.
  const userIdRef = useRef<string | undefined>(user?.userId);
  const navigate = useNavigate();
  const scheduleTimeout = typeof window !== 'undefined' ? window.setTimeout : setTimeout;
  // One close-fn per active job; replaced when the socket reconnects.
  const socketsRef = useRef<Map<string, () => void>>(new Map());
  // Jobs whose WebSocket failed; these fall back to REST polling.
  const fallbackJobsRef = useRef<Set<string>>(new Set());
  const fallbackPollerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const notifiedJobsRef = useRef<Set<string>>(new Set());
  // Consecutive REST-fallback fetch failures per job; after N strikes we surface
  // a degraded signal so a job behind a down/403 endpoint isn't silently frozen.
  const fallbackFailuresRef = useRef<Map<string, number>>(new Map());

  const forgetJob = useCallback((jobId: string) => {
    const closeSocket = socketsRef.current.get(jobId);
    if (closeSocket) {
      closeSocket();
      socketsRef.current.delete(jobId);
    }
    fallbackJobsRef.current.delete(jobId);
    fallbackFailuresRef.current.delete(jobId);
    notifiedJobsRef.current.delete(jobId);
    if (fallbackJobsRef.current.size === 0 && fallbackPollerRef.current !== null) {
      clearInterval(fallbackPollerRef.current);
      fallbackPollerRef.current = null;
    }
    const userId = userIdRef.current;
    if (userId) {
      removeLibraryItem(userId, jobId);
    }
    setJobs((prev) => prev.filter((job) => job.id !== jobId));
  }, []);

  // Populate active and completed jobs for this user from database on mount/login
  useEffect(() => {
    if (!user?.userId) {
      setJobs([]);
      return;
    }

    /* Whatever is in the list belongs to whoever was signed in a moment ago,
       and this effect only re-runs when that changes. Clearing here rather than
       relying on the response to overwrite is what lets the merge below be a
       merge: it can then assume everything it finds was submitted under the
       current account, during this fetch. */
    setJobs([]);

    let active = true;
    // Use the merged library service (API + per-user cache) so hydrated jobs carry
    // the real cached title/text rather than the id-prefix placeholder the minimal
    // GET /videos response would otherwise produce. Only seed from a real backend
    // fetch — hydrating cached pending jobs during an outage would let the poller's
    // error path immediately mark them 'failed'.
    fetchLibraryItems(user.userId)
      .then((result) => {
        if (!active) return;
        if (result.source !== 'api') return;
        /* Merge, not replace. This fetch races the user: on a cold load they
           can submit before GET /videos answers, submitJob appends the new job,
           and a replace here dropped it — no socket, no progress, nothing in
           the rail, for a generation the engine was already running. It is the
           same race LOCAL_ONLY_GRACE_MS exists to cover on the library side.
           The backend list wins wherever the two hold the same id, since it is
           authoritative for everything except a job it has not listed yet. */
        setJobs((prev) => {
          const hydrated = result.items.map(libraryItemToJob);
          const hydratedIds = new Set(hydrated.map((job) => job.id));
          return [...hydrated, ...prev.filter((job) => !hydratedIds.has(job.id))];
        });
      })
      .catch((err) => {
        logger.warn('Failed to hydrate initial user jobs:', err);
      });

    return () => {
      active = false;
    };
  }, [user?.userId]);

  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  useEffect(() => {
    userIdRef.current = user?.userId;
  }, [user]);

  const updateLibraryFromJob = useCallback((job: ProcessingJob, status: ProcessingStatus) => {
    const userId = userIdRef.current;
    if (!userId) return;
    const existing = fetchLibraryItem(userId, job.id);
    const baseItem: LibraryItem = existing ?? {
      id: job.id,
      title: job.title,
      createdAt: new Date(job.submittedAt).toISOString(),
      description: describeFromText(job.text),
      sourceText: job.text,
      /* No `durationSeconds` here, and that is the point. The two types use the
         name for different things — see hydrateJob above: on a ProcessingJob it
         is the requested seconds *per scene*, on a LibraryItem it is the
         finished video's runtime. Copying one into the other badged every
         submitted job with its own per-scene setting, so a cancelled job that
         never rendered a frame still claimed to be 0:06 long.

         Left unset, LibraryCard falls back to the loaded <video> element's own
         duration, which is the only runtime the client can actually know until
         the engine persists one. The per-scene setting is not lost: it travels
         in `metadata`, which is where hydrateJob reads it back from. */
      aspectRatio: job.aspectRatio,
    };

    const next: LibraryItem = {
      ...baseItem,
      videoUrl: job.videoUrl ?? baseItem.videoUrl,
      thumbnailUrl: job.thumbnailUrl ?? baseItem.thumbnailUrl,
      status,
      // `durationSeconds` rides the spread, so a real runtime already on the
      // item — from the API, once it sends one — survives this update.
      metadata: job.metadata ?? baseItem.metadata,
    };
    upsertLibraryItem(userId, next);
  }, []);

  // Re-run when the user resolves too: ProcessingProvider mounts above the auth
  // gate, so on reload `jobs` is hydrated before boot sets the user. Without
  // user?.userId in the deps the persisted jobs would never be mirrored into the
  // per-user library for this session.
  //
  // Mirrors only the jobs whose identity changed since the last pass. Every
  // mirror is a JSON.parse + JSON.stringify of the whole library array, and a
  // WebSocket frame for one job produces a new `jobs` array containing N-1
  // untouched jobs — re-mirroring all of them did N full serialisations per
  // frame for one job's worth of new information. applyStatusUpdate keeps the
  // untouched jobs' object identities, which is what makes this check honest.
  // A user change clears the memory so everything mirrors once into the new
  // user's library.
  const mirroredJobsRef = useRef(new Map<string, ProcessingJob>());
  const mirroredUserRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (mirroredUserRef.current !== user?.userId) {
      mirroredUserRef.current = user?.userId;
      mirroredJobsRef.current.clear();
    }
    if (!user?.userId) return;
    const seen = new Set<string>();
    jobs.forEach((job) => {
      seen.add(job.id);
      if (mirroredJobsRef.current.get(job.id) === job) return;
      updateLibraryFromJob(job, job.status);
      mirroredJobsRef.current.set(job.id, job);
    });
    // Forget jobs that left the list, so a re-added id mirrors fresh.
    for (const jobId of Array.from(mirroredJobsRef.current.keys())) {
      if (!seen.has(jobId)) mirroredJobsRef.current.delete(jobId);
    }
  }, [jobs, user?.userId, updateLibraryFromJob]);

  const handleJobCompletion = useCallback((job: ProcessingJob, videoUrl?: string) => {
    if (notifiedJobsRef.current.has(job.id)) {
      return;
    }
    notifiedJobsRef.current.add(job.id);

    pushNotification({
      message: `${job.title} is ready`,
      actionLabel: 'View video',
      onAction: () => navigate(`/videos/${job.videoId ?? job.id}`),
    });
    const userId = userIdRef.current;
    if (userId) {
      /* Both fields are optional here — a completion frame need not carry a
         download_url, and mergeMetadata returns undefined when neither side has
         anything. updateLibraryItemStatus drops undefined values rather than
         writing them over the cached entry, so this says "status is completed,
         and here is anything else I happen to know" rather than blanking a
         videoUrl an earlier REST read had already filed. */
      updateLibraryItemStatus(userId, job.id, 'completed', { videoUrl, metadata: job.metadata });
    }
  }, [navigate, pushNotification]);

  // Stable string key for the set of active job IDs. Changes only when a job
  // enters or leaves the queued/processing state, so the socket-management
  // effect skips re-runs caused by status-update renders for already-connected jobs.
  const activeJobIdsKey = useMemo(
    () =>
      jobs
        .filter((j) => !isTerminalStatus(j.status))
        .map((j) => j.id)
        .sort()
        .join(','),
    [jobs],
  );

  // Keep the wallet balance in step with the jobs holding money against it.
  //
  // A submission places a hold (billing debits the balance immediately) and a job
  // reaching a terminal state settles or releases it — both move the balance without
  // the user doing anything on the wallet page, and neither was reflected until a full
  // reload. activeJobIdsKey is exactly the right trigger: it changes when, and only
  // when, a job enters or leaves queued/processing.
  //
  // One refresh is enough on the terminal edge: the pipeline settles the hold BEFORE
  // it broadcasts the status event (story_pipeline._complete_job / _fail_job call
  // _settle_credits, then pg_notify), so by the time we observe the transition the
  // balance is already final. No polling needed.
  const didInitialBalanceSyncRef = useRef(false);
  useEffect(() => {
    // Skip the mount pass — WalletProvider's own initial load already fetched it.
    if (!didInitialBalanceSyncRef.current) {
      didInitialBalanceSyncRef.current = true;
      return;
    }
    // Best-effort: a stale balance is a display nuisance, never a reason to surface
    // an error over a job update the user actually cares about.
    void refreshBalance().catch((err) => {
      logger.warn('Failed to refresh wallet balance after job change:', err);
    });
  }, [activeJobIdsKey, refreshBalance]);

  // Apply a single-job status update received from a WebSocket message or the
  // REST fallback poller. Mirrors the state-merge logic of the old polling loop.
  const applyStatusUpdate = useCallback(
    (jobId: string, result: JobStatusResult) => {
      setJobs((prev) => {
        const newlyCompleted: ProcessingJob[] = [];
        const nextJobs = prev.map((job) => {
          if (job.id !== jobId) return job;
          if (job.status === 'cancelled') return job;
          const nextStatus = result.status;
          const videoUrl = result.videoUrl ?? job.videoUrl;
          const updated: ProcessingJob = {
            ...job,
            title: result.title ?? job.title,
            status: nextStatus,
            // Only the structured error message — it comes from the backend's
            // curated catalog. The legacy `detail` string is a raw relay (the WS
            // path forwards pipeline text verbatim) and must not reach the modal.
            errorDetail: result.error?.message,
            // The structured form, for ContentPage — which mirrors this job
            // instead of opening its own socket, and needs the code and
            // `retryable`, not just the message.
            error: result.error ?? job.error,
            videoUrl,
            thumbnailUrl: result.thumbnailUrl ?? job.thumbnailUrl,
            durationSeconds: result.durationSeconds ?? job.durationSeconds,
            metadata: mergeMetadata(job.metadata, result.metadata),
            // Once progress goes missing (job turned terminal) keep the last known
            // snapshot rather than blanking the checklist right before completion.
            progress: result.progress ?? job.progress,
            estimatedCost: result.estimatedCost ?? job.estimatedCost,
            estimatedDurationSeconds: result.estimatedDurationSeconds ?? job.estimatedDurationSeconds,
          };
          /* No library write here, deliberately. This updater must stay pure —
             StrictMode double-invokes it, and a concurrent render can invoke it
             for a pass React throws away, persisting a status the UI never
             showed. The mirror effect above watches committed `jobs` and
             already covers exactly this write. */
          if (nextStatus === 'completed' && job.status !== 'completed') {
            newlyCompleted.push(updated);
          }
          return updated;
        });

        if (newlyCompleted.length > 0) {
          scheduleTimeout(() => {
            newlyCompleted.forEach((item) => {
              // Presence, not the URL: item.videoUrl is a presigned S3 link, which is
              // bearer-equivalent — anyone holding it can fetch the user's video until
              // it expires. The id is enough to correlate.
              logger.log(`Job completed: ${item.id}`, {
                hasVideoUrl: Boolean(item.videoUrl),
              });
              handleJobCompletion(item, item.videoUrl);
            });
          }, 0);
        }

        return nextJobs;
      });
    },
    [handleJobCompletion, scheduleTimeout],
  );

  // Keep the latest applyStatusUpdate reachable from the long-lived fallback
  // interval without restarting it. The interval closes over pollFallbackJob
  // (stable), which reads this ref — so status updates always dispatch through
  // the current callback (with the current handleJobCompletion/updateLibraryFromJob),
  // never a stale closure frozen at interval-creation time.
  const applyStatusUpdateRef = useRef(applyStatusUpdate);
  useEffect(() => {
    applyStatusUpdateRef.current = applyStatusUpdate;
  }, [applyStatusUpdate]);

  // Poll one job once via REST. Stable identity (reads refs, not props) so the
  // shared interval never needs to be torn down and recreated.
  const MAX_FALLBACK_STRIKES = 3;
  const pollFallbackJob = useCallback(async (jobId: string) => {
    try {
      const result = await fetchVideoStatus(jobId);
      fallbackFailuresRef.current.delete(jobId);
      applyStatusUpdateRef.current(jobId, result);
      if (isTerminalStatus(result.status)) {
        fallbackJobsRef.current.delete(jobId);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        forgetJob(jobId);
        return;
      }
      // Surface a degraded signal after repeated failures instead of leaving the
      // job silently frozen (e.g. behind a deleted/403/persistent-5xx endpoint).
      const strikes = (fallbackFailuresRef.current.get(jobId) ?? 0) + 1;
      fallbackFailuresRef.current.set(jobId, strikes);
      // The user-facing message below is deliberately vague ("retrying…") because a
      // transient blip is the common case. But the cause used to be discarded
      // entirely, so a permanent 403 or 404 looked identical to a flaky network and
      // left nothing to diagnose from. Logged on the first strike only, so a job
      // polling every 15s doesn't repeat the same failure forever.
      if (strikes === 1) {
        logger.warn(`REST fallback poll failed for job ${jobId}:`, err);
      }
      if (strikes >= MAX_FALLBACK_STRIKES) {
        setJobs((prev) =>
          prev.map((job) =>
            job.id === jobId
              ? { ...job, errorDetail: 'Unable to fetch status — retrying…' }
              : job,
          ),
        );
      }
    }
  }, [forgetJob]);

  // Start the shared 15s REST fallback poller for jobs whose WebSocket failed.
  // Idempotent — safe to call from multiple onFallback callbacks.
  const ensureFallbackPoller = useCallback(() => {
    if (fallbackPollerRef.current !== null) return;
    fallbackPollerRef.current = setInterval(() => {
      const jobIds = [...fallbackJobsRef.current];
      if (jobIds.length === 0) {
        clearInterval(fallbackPollerRef.current!);
        fallbackPollerRef.current = null;
        return;
      }
      Promise.all(jobIds.map((jobId) => pollFallbackJob(jobId))).catch(() => { });
    }, 15_000);
  }, [pollFallbackJob]);

  // Open/close sockets as the set of active jobs changes. Each active job
  // gets exactly one socket; jobs whose socket exhausts its retries move to
  // the shared fallback REST poller instead.
  useEffect(() => {
    const currentSockets = socketsRef.current;
    const fallbackJobs = fallbackJobsRef.current;

    if (!user) {
      for (const close of currentSockets.values()) close();
      currentSockets.clear();
      fallbackJobs.clear();
      if (fallbackPollerRef.current !== null) {
        clearInterval(fallbackPollerRef.current);
        fallbackPollerRef.current = null;
      }
      return;
    }

    const activeIds = new Set(activeJobIdsKey ? activeJobIdsKey.split(',') : []);

    // Close sockets and remove fallback entries for jobs no longer active.
    for (const [jobId, close] of currentSockets) {
      if (!activeIds.has(jobId)) {
        close();
        currentSockets.delete(jobId);
      }
    }
    for (const jobId of fallbackJobs) {
      if (!activeIds.has(jobId)) fallbackJobs.delete(jobId);
    }

    // Open a socket for each newly-active job not already covered.
    for (const jobId of activeIds) {
      if (currentSockets.has(jobId) || fallbackJobs.has(jobId)) continue;
      const close = openVideoSocket(
        jobId,
        (result) => { applyStatusUpdate(jobId, result); },
        () => {
          fallbackJobs.add(jobId);
          // Poll immediately so there's no ~15s gap (on top of the WS backoff
          // already elapsed) before the interval's first tick.
          void pollFallbackJob(jobId);
          ensureFallbackPoller();
        },
      );
      currentSockets.set(jobId, close);
    }
  }, [activeJobIdsKey, user, applyStatusUpdate, ensureFallbackPoller, pollFallbackJob]);

  /* Resume, confirm and cancel are the same operation with three endpoints:
     call it, put the returned status on the job, and mirror it to the library.
     They were three verbatim copies of this block, which is how a change lands
     in two of the three — this file's own hydrateJob comment ("Shared … so they
     can't diverge") states the rule the copies were breaking.

     Seeding from the cache is the part worth keeping honest: the job need not
     be in this session's list at all — resuming from a library card for an item
     we only have cached is the ordinary case — and without the seed the poller
     has nothing to pick up, leaving a resumed job stuck reading 'failed'. The
     cache read moves out of the updater while the three become one, for the
     reason applyStatusUpdate gives: an updater React may run for a discarded
     pass has no business touching storage. */
  const transitionJob = useCallback(
    async (jobId: string, call: (id: string) => Promise<{ status: ProcessingStatus }>) => {
      const { status } = await call(jobId);
      const userId = userIdRef.current;
      const cached = userId ? fetchLibraryItem(userId, jobId) : undefined;
      setJobs((prev) => {
        if (prev.some((job) => job.id === jobId)) {
          return prev.map((job) =>
            job.id === jobId ? { ...job, status, errorDetail: undefined } : job,
          );
        }
        if (!cached) return prev;
        return [...prev, { ...libraryItemToJob(cached), status, errorDetail: undefined }];
      });
      if (userId) {
        updateLibraryItemStatus(userId, jobId, status, {});
      }
    },
    [],
  );

  // Close all sockets when the provider unmounts.
  useEffect(() => {
    const currentSockets = socketsRef.current;
    return () => {
      for (const close of currentSockets.values()) close();
      currentSockets.clear();
      if (fallbackPollerRef.current !== null) {
        clearInterval(fallbackPollerRef.current);
        fallbackPollerRef.current = null;
      }
    };
  }, []);

  const value = useMemo<ProcessingContextValue>(() => ({
    jobs,
    submitJob: async (payload: SubmissionPayload) => {
      if (!user) {
        throw new UserFacingError('Please sign in again to submit.');
      }
      // Attach the display name for backend attribution (optional `author`).
      if (!payload.author && user.displayName) {
        payload.author = user.displayName;
      }
      const idempotencyKey = newVideoSubmitIdempotencyKey();
      const response = await submitToValoiEngine(payload, user.userId, idempotencyKey);
      const newJob: ProcessingJob = {
        id: response.jobId,
        // Mirror the backend's behaviour: when no title was typed, derive one from
        // the text so the queue/notification show something meaningful instead of
        // a placeholder. An uploaded file has no text yet (the backend extracts it),
        // so fall back to its filename. The poller below syncs the backend's title.
        title:
          payload.title?.trim() ||
          (payload.text?.trim() ? titleFromText(payload.text) : payload.fileName) ||
          'Untitled',
        status: response.status === 'completed' ? 'completed' : 'queued',
        etaSeconds: response.etaSeconds,
        submittedAt: Date.now(),
        videoId: response.videoId,
        aspectRatio: response.aspectRatio,
        durationSeconds: response.durationSeconds,
        text: payload.text,
        metadata: response.metadata,
      };

      setJobs((prev) => [...prev, newJob]);
      updateLibraryFromJob(newJob, newJob.status);

      if (newJob.status === 'completed') {
        handleJobCompletion(newJob);
      }
      return response.jobId;
    },
    resumeJob: (jobId: string) => transitionJob(jobId, resumeVideo),
    confirmJob: (jobId: string) => transitionJob(jobId, confirmVideo),
    cancelJob: (jobId: string) => transitionJob(jobId, cancelVideo),
    /* Not a transition, so it does not go through `transitionJob`: that helper
       exists to write a *new status* onto a job and keep the library row in
       step. There is no status after deletion and no row to keep — the job
       leaves the queue and the library entry goes with it. */
    deleteJob: async (jobId: string) => {
      await deleteVideo(jobId);
      forgetJob(jobId);
    },
    forgetJob,
  }), [jobs, handleJobCompletion, updateLibraryFromJob, transitionJob, user, forgetJob]);

  return <ProcessingContext.Provider value={value}>{children}</ProcessingContext.Provider>;
}
